import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  ButtonModule,
  CheckboxesModule,
  DropdownItem,
  DropdownModule,
  IconModule,
  InputContainerModule,
  MessageModule,
  ModalModule,
  ModalService,
} from '@vinnova/ui';
import { interval, Subscription } from 'rxjs';
import {
  ExcelImportExcludedTableComponent,
  ExcelImportExcludedTableTexts,
} from '../excel-import-excluded-table/excel-import-excluded-table.component';
import { ExcelImportPreviewTableComponent } from '../excel-import-preview-table/excel-import-preview-table.component';
import {
  ExcelImportAnalyzeFn,
  ExcelImportBuildPreviewFn,
  ExcelImportExcludedEntry,
  ExcelImportExcludedReason,
  ExcelImportGetExcludedFn,
  ExcelImportGetReconciliationFn,
  ExcelImportGetStringsFn,
  ExcelImportGetSummaryFn,
  ExcelImportGetTimingsFn,
  ExcelImportGetUnitFn,
  ExcelImportModalTexts,
  ExcelImportPreviewPart,
  ExcelImportReasoningEffort,
  ExcelImportReconciliation,
  ExcelImportSummary,
  ExcelImportToApplyResultFn,
  ExcelImportUnit,
} from './excel-import-modal.model';

type ImportState = 'upload' | 'loading' | 'preview' | 'error';

const ACCEPTED_EXTENSIONS = ['.xlsx', '.xlsm'];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
/** Reasoning-model latency varies a lot per file; past this point we tell the user it is slow rather than broken. */
const SLOW_THRESHOLD_SECONDS = 15;

/**
 * Generic upload → analyse → preview → apply flow for Excel-based AI imports (budget, upparbetade kostnader).
 * The domain specifics (schema, HTTP call, result shape) are plugged in by the concrete wrapper component as
 * plain functions, so this shell never needs to know about BudgetSlotSchema/UpparbetadeKostnaderSlotSchema etc.
 */
@UntilDestroy()
@Component({
  selector: 'lib-excel-import-modal-shell',
  templateUrl: './excel-import-modal-shell.component.html',
  styleUrl: './excel-import-modal-shell.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ModalModule,
    ButtonModule,
    DropdownModule,
    CheckboxesModule,
    IconModule,
    InputContainerModule,
    MessageModule,
    ExcelImportPreviewTableComponent,
    ExcelImportExcludedTableComponent,
  ],
})
export class ExcelImportModalShellComponent implements OnInit {
  readonly modalService = inject(ModalService);

  // Set via ModalService initialState (a plain instance[key] = value assignment, not Angular's input
  // binding), so these must be plain properties rather than signal input()s.
  texts: ExcelImportModalTexts;
  years: number[] = [];
  columnLabels: Record<number, string> = {};
  deploymentItems: DropdownItem[] = [];
  defaultDeployment?: string;
  supportsPreprocessing = false;
  supportsDebugToggle = false;
  analyzeFn: ExcelImportAnalyzeFn;
  buildPreviewFn: ExcelImportBuildPreviewFn;
  getUnitFn: ExcelImportGetUnitFn;
  getUnmatchedBlocksFn: ExcelImportGetStringsFn;
  getWarningsFn: ExcelImportGetStringsFn;
  toApplyResultFn: ExcelImportToApplyResultFn;
  getSummaryFn?: ExcelImportGetSummaryFn;
  getReconciliationFn?: ExcelImportGetReconciliationFn;
  getExcludedFn?: ExcelImportGetExcludedFn;
  getTimingsFn?: ExcelImportGetTimingsFn;

  readonly applied = output<unknown>();

  readonly state = signal<ImportState>('upload');
  readonly selectedFile = signal<File | undefined>(undefined);
  readonly fileError = signal<string | undefined>(undefined);
  /** Server-provided reason for a 400 (e.g. file too large); undefined falls back to the generic text. */
  readonly errorMessage = signal<string | undefined>(undefined);
  readonly result = signal<unknown>(undefined);
  readonly unit = signal<ExcelImportUnit>('SEK');
  readonly reasoningEffort = signal<ExcelImportReasoningEffort>('low');
  /** undefined uses the server-configured default deployment. */
  readonly deployment = signal<string | undefined>('gpt-5.6-luna');
  readonly preprocessing = signal(true);
  readonly showDebug = signal(false);
  readonly includedParts = signal<Record<string, boolean>>({});
  readonly elapsedSeconds = signal(0);
  readonly isSlow = computed(
    () => this.elapsedSeconds() >= SLOW_THRESHOLD_SECONDS,
  );

  private analyzeSubscription?: Subscription;
  private timerSubscription?: Subscription;

  // initialState is assigned before the first change detection, so the default is available here but not in the field initializer.
  ngOnInit(): void {
    if (this.defaultDeployment !== undefined) {
      this.deployment.set(this.defaultDeployment);
    }
  }

  readonly unitItems = computed<DropdownItem[]>(() => {
    const t = this.texts;
    return [
      { value: 'SEK', label: t.unitSek },
      { value: 'TKR', label: t.unitTkr },
      { value: 'MSEK', label: t.unitMsek },
    ];
  });

  readonly reasoningItems = computed<DropdownItem[]>(() => {
    const t = this.texts;
    return [
      { value: 'none', label: t.reasoningNone },
      { value: 'minimal', label: t.reasoningMinimal },
      { value: 'low', label: t.reasoningLow },
      { value: 'medium', label: t.reasoningMedium },
      { value: 'high', label: t.reasoningHigh },
    ];
  });

  readonly preview = computed<ExcelImportPreviewPart[]>(() => {
    const result = this.result();
    return result === undefined ? [] : this.buildPreviewFn(result, this.unit());
  });

  readonly unmatchedBlocks = computed<string[]>(() => {
    const result = this.result();
    return result === undefined ? [] : this.getUnmatchedBlocksFn(result);
  });

  readonly warnings = computed<string[]>(() => {
    const result = this.result();
    return result === undefined ? [] : this.getWarningsFn(result);
  });

  readonly summary = computed<ExcelImportSummary | undefined>(() => {
    const result = this.result();
    return result === undefined || !this.getSummaryFn
      ? undefined
      : this.getSummaryFn(result);
  });

  readonly excluded = computed<ExcelImportExcludedEntry[]>(() => {
    const result = this.result();
    return result === undefined || !this.getExcludedFn
      ? []
      : this.getExcludedFn(result, this.unit());
  });

  /** Rows the backend could not attribute to any part. */
  readonly excludedWithoutPart = computed<ExcelImportExcludedEntry[]>(() =>
    this.excluded().filter((e) => e.partId === undefined),
  );

  /** Seconds per phase; "rest" is everything server-side that was not preprocessing (mainly the extraction call). */
  readonly timings = computed(() => {
    const result = this.result();
    const t =
      result === undefined || !this.getTimingsFn
        ? undefined
        : this.getTimingsFn(result);
    if (!t) {
      return;
    }
    return {
      preprocessing: t.preprocessingMs / 1000,
      rest: (t.totalMs - t.preprocessingMs) / 1000,
      serverTotal: t.totalMs / 1000,
      roundTrip: this.elapsedSeconds(),
    };
  });

  readonly canApply = computed(
    () =>
      this.state() === 'preview' &&
      this.preview().some(
        (p) => p.hasImportedValues && this.includedParts()[p.part.id],
      ),
  );

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    this.fileError.set(undefined);

    if (!file) {
      this.selectedFile.set(undefined);
      return;
    }

    const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(extension)) {
      this.fileError.set(this.texts.fileHelp);
      this.selectedFile.set(undefined);
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      this.fileError.set(this.texts.fileHelp);
      this.selectedFile.set(undefined);
      return;
    }

    this.selectedFile.set(file);
  }

  analyze(): void {
    const file = this.selectedFile();
    if (!file) {
      return;
    }

    this.state.set('loading');
    this.errorMessage.set(undefined);
    this.startTimer();
    this.analyzeSubscription = this.analyzeFn(file, {
      reasoningEffort: this.reasoningEffort(),
      deployment: this.deployment(),
      preprocessing: this.supportsPreprocessing
        ? this.preprocessing()
        : undefined,
    })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (result) => {
          this.stopTimer();
          this.result.set(result);
          const unit = this.getUnitFn(result);
          this.unit.set(unit);
          const parts = this.buildPreviewFn(result, unit);
          this.includedParts.set(
            Object.fromEntries(
              parts.map((p) => [p.part.id, p.hasImportedValues]),
            ),
          );
          this.state.set('preview');
        },
        error: (error: unknown) => {
          this.stopTimer();
          this.errorMessage.set(this.validationDetail(error));
          this.state.set('error');
        },
      });
  }

  /** Unsubscribing aborts the HTTP request so the backend can cancel the model call. Keeps the file so the user can retry with another effort level. */
  cancelAnalyze(): void {
    this.analyzeSubscription?.unsubscribe();
    this.analyzeSubscription = undefined;
    this.stopTimer();
    this.state.set('upload');
  }

  reset(): void {
    this.result.set(undefined);
    this.selectedFile.set(undefined);
    this.fileError.set(undefined);
    this.errorMessage.set(undefined);
    this.state.set('upload');
  }

  private validationDetail(error: unknown): string | undefined {
    if (!(error instanceof HttpErrorResponse) || error.status !== 400) {
      return undefined;
    }
    const detail = (error.error as { detail?: unknown } | null)?.detail;
    return typeof detail === 'string' && detail.trim() ? detail : undefined;
  }

  private startTimer(): void {
    this.elapsedSeconds.set(0);
    this.timerSubscription = interval(1000)
      .pipe(untilDestroyed(this))
      .subscribe(() => this.elapsedSeconds.update((s) => s + 1));
  }

  private stopTimer(): void {
    this.timerSubscription?.unsubscribe();
    this.timerSubscription = undefined;
  }

  setUnit(value: string): void {
    this.unit.set(value as ExcelImportUnit);
  }

  setReasoningEffort(value: string): void {
    this.reasoningEffort.set(value as ExcelImportReasoningEffort);
  }

  setDeployment(value: string): void {
    this.deployment.set(value || undefined);
  }

  setPreprocessing(value: boolean): void {
    this.preprocessing.set(value);
  }

  setShowDebug(value: boolean): void {
    this.showDebug.set(value);
  }

  setIncluded(partId: string, included: boolean): void {
    this.includedParts.update((parts) => ({ ...parts, [partId]: included }));
  }

  reconciliationFor(partId: string): ExcelImportReconciliation | undefined {
    const result = this.result();
    return result === undefined || !this.getReconciliationFn
      ? undefined
      : this.getReconciliationFn(result, partId, this.unit());
  }

  excludedForPart(partId: string): ExcelImportExcludedEntry[] {
    return this.excluded().filter((e) => e.partId === partId);
  }

  private reasonLabel(reason: ExcelImportExcludedReason): string {
    const t = this.texts;
    const labels: Record<ExcelImportExcludedReason, string | undefined> = {
      outsidePeriod: t.reasonOutsidePeriod,
      unmatchedCategory: t.reasonUnmatchedCategory,
      partialOverlap: t.reasonPartialOverlap,
    };
    return labels[reason] ?? reason;
  }

  readonly excludedTexts = computed<ExcelImportExcludedTableTexts>(() => {
    const t = this.texts;
    return {
      source: t.excludedSource ?? '',
      date: t.excludedDate ?? '',
      label: t.excludedLabel ?? '',
      amount: t.excludedAmount ?? '',
      reason: t.excludedReason ?? '',
      reasonLabels: {
        outsidePeriod: this.reasonLabel('outsidePeriod'),
        unmatchedCategory: this.reasonLabel('unmatchedCategory'),
        partialOverlap: this.reasonLabel('partialOverlap'),
      },
    };
  });

  apply(): void {
    if (!this.canApply()) {
      return;
    }
    const included = this.preview().filter(
      (p) => p.hasImportedValues && this.includedParts()[p.part.id],
    );
    this.applied.emit(this.toApplyResultFn(included));
    this.modalService.hide();
  }

  confidenceLabel(confidence?: number): string {
    if (confidence === undefined) {
      return '';
    }
    const t = this.texts;
    if (confidence >= 0.8) {
      return t.confidenceHigh;
    }
    if (confidence >= 0.6) {
      return t.confidenceMedium;
    }
    return t.confidenceLow;
  }

  confidenceClass(confidence?: number): string {
    if (confidence === undefined) {
      return '';
    }
    if (confidence >= 0.8) {
      return 'text-bg-success';
    }
    if (confidence >= 0.6) {
      return 'text-bg-warning';
    }
    return 'text-bg-danger';
  }
}
